import { getAccessToken } from "../api";

export interface MapPoint {
  id: string;
  kind: "driver" | "delivery";
  label: string;
  status: string;
  position: [number, number] | null;
  locationAt: string | null;
}
export interface MapSnapshot {
  points: MapPoint[];
  receivedAt: string;
  driverRows: number;
  deliveryRows: number;
  boardCap: number;
  truncated: boolean;
}
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_ROWS = 2000;
const invalid = () => new Error("The map response is incomplete or invalid. No new locations were applied.");
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
function text(v: unknown, nullable = false): string {
  if (v === null && nullable) return "";
  if (typeof v !== "string" || v.length > 1000 || (!nullable && !v.trim())) throw invalid();
  return v;
}
function count(v: unknown): number {
  if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) throw invalid();
  return v;
}
function rows(v: unknown): Record<string, unknown>[] {
  if (!Array.isArray(v) || v.length > MAX_ROWS || !v.every(object)) throw invalid();
  return v;
}
function position(row: Record<string, unknown>): [number, number] | null {
  const { lat, lng } = row;
  // Invalid coordinates remain in the textual list; never coerce zero/string.
  return typeof lat === "number" && Number.isFinite(lat) && Math.abs(lat) <= 90 &&
    typeof lng === "number" && Number.isFinite(lng) && Math.abs(lng) <= 180 ? [lat, lng] : null;
}
function locationAt(v: unknown): string | null {
  if (v === null) return null;
  if (typeof v !== "string" || v.length > 40 || !/^\d{4}-\d{2}-\d{2}T/.test(v) || !Number.isFinite(Date.parse(v))) throw invalid();
  return v;
}

/** Separate source reads, not one transactionally consistent board. */
export function decodeMapSnapshot(fleet: unknown, board: unknown, receivedAt: string): MapSnapshot {
  if (!object(fleet) || !object(board) || !object(board.lanes) || !object(board.counts) ||
      typeof board.truncated !== "boolean") throw invalid();
  const drivers = rows(fleet.drivers);
  const deliveries: Record<string, unknown>[] = [];
  const boardCap = count(board.cap);
  if (boardCap === 0 || boardCap > MAX_ROWS) throw invalid();
  for (const lane of ["queue", "active", "attention"]) {
    const page = rows(board.lanes[lane]);
    if (count(board.counts[lane]) !== page.length) throw invalid();
    deliveries.push(...page);
  }
  if (deliveries.length > boardCap) throw invalid();
  const ids = new Set<string>();
  function id(row: Record<string, unknown>, kind: string): string {
    const value = text(row.id);
    if (value.length > 100 || ids.has(`${kind}:${value}`)) throw invalid();
    ids.add(`${kind}:${value}`);
    return value;
  }
  const points: MapPoint[] = drivers.map((row) => ({
    id: id(row, "driver"), kind: "driver", status: text(row.status), position: position(row),
    label: `${text(row.name)} · ${text(row.status)} · ${count(row.active_deliveries)} active`,
    locationAt: locationAt(row.last_location_at),
  }));
  points.push(...deliveries.map((row): MapPoint => ({
    id: id(row, "delivery"), kind: "delivery", status: text(row.status), position: position(row),
    label: `${text(row.order_name, true) || "Order"} · ${text(row.customer_name, true)} · ${text(row.status)}`,
    locationAt: null, // Address, not a live vehicle fix.
  })));
  return { points, receivedAt, driverRows: drivers.length, deliveryRows: deliveries.length,
    boardCap, truncated: board.truncated };
}

/** Leaflet string tooltips parse HTML. Use a text-only DOM node. */
export function mapLabelNode(label: string): HTMLElement {
  const node = document.createElement("span");
  node.textContent = label;
  return node;
}
function untilAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(new Error("Map read cancelled or timed out."));
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}
async function read(path: "/dispatch/fleet" | "/dispatch/board", token: string, signal: AbortSignal): Promise<unknown> {
  if (signal.aborted || getAccessToken() !== token) throw new Error("Console session changed. Refresh after signing in.");
  const response = await untilAbort(fetch(`/api/v1/console${path}`, {
    method: "GET", credentials: "include", redirect: "error", cache: "no-store", signal,
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  }), signal);
  // A background map read must not refresh/replay under another account.
  if (response.status === 401 || response.status === 403) {
    void response.body?.cancel().catch(() => {});
    throw new Error("Map access could not be verified. Sign in again or check console permissions.");
  }
  if (!response.ok || response.headers.get("content-type")?.split(";")[0].trim().toLowerCase() !== "application/json") {
    void response.body?.cancel().catch(() => {});
    throw invalid();
  }
  const length = response.headers.get("content-length");
  if (length !== null && (!/^\d+$/.test(length) || Number(length) > MAX_BYTES)) {
    void response.body?.cancel().catch(() => {});
    throw invalid();
  }
  const reader = response.body?.getReader();
  if (!reader) throw invalid();
  let body = "";
  let bytes = 0;
  const decoder = new TextDecoder("utf-8", { fatal: true });
  try {
    for (;;) {
      const chunk = await untilAbort(reader.read(), signal);
      if (chunk.done) break;
      bytes += chunk.value.byteLength;
      if (bytes > MAX_BYTES || signal.aborted || getAccessToken() !== token) throw invalid();
      body += decoder.decode(chunk.value, { stream: true });
    }
    body += decoder.decode();
  } finally {
    void reader.cancel().catch(() => {});
    reader.releaseLock();
  }
  if (signal.aborted || getAccessToken() !== token) throw new Error("Console session changed. Refresh after signing in.");
  const envelope: unknown = JSON.parse(body);
  if (!object(envelope) || envelope.success !== true || !object(envelope.data)) throw invalid();
  return envelope.data;
}
export async function loadMapSnapshot(signal: AbortSignal): Promise<MapSnapshot> {
  const token = getAccessToken();
  if (!token || signal.aborted) throw new Error("Sign in to read the fleet map.");
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 12000);
  try {
    const [fleet, board] = await Promise.all([
      read("/dispatch/fleet", token, controller.signal), read("/dispatch/board", token, controller.signal),
    ]);
    if (signal.aborted || controller.signal.aborted || getAccessToken() !== token) throw invalid();
    return decodeMapSnapshot(fleet, board, new Date().toISOString());
  } finally {
    abort();
    clearTimeout(timer);
    signal.removeEventListener("abort", abort);
  }
}
